import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NavigationActions, NavigationApiActions } from '@common/store/navigation/actions/navigation.actions';
import { Navigation, NavigationGroup, NavigationGroupTypes, NavigationItem, NavigationItemTypes, NavigationSection, NavigationSectionTypes } from '@common/store/navigation/models/navigation.models';
import {
    selectCurrentApplication,
    selectCurrentItem,
    selectCurrentTeam,
    selectEnterpriseGroup,
    selectNavigateToGroup,
    selectNavigateToItem,
    selectNavigateToSection,
    selectNavigateToURL,
    selectNavigationData,
    selectNavigationGroups,
    selectNavigationLoaded,
    selectNavigationRaw,
    selectPersonalGroup,
    selectSystemGroup,
    selectSystemSection,
    selectSystemSingleViewStateItem,
    selectTeamGroup,
} from '@common/store/navigation/selectors/navigation.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { AddViewToNavigationComponent } from '@src/suite/common/components/planning-views/add-view-to-navigation/add-view-to-navigation';
import { Path } from '@src/suite/common/data-models/complex-types/path';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ViewStateFilterSerializer, ViewStateOptionsSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { ServiceRecord } from '@src/suite/common/data-models/service/service-record';
import { ViewStateServiceRecord } from '@src/suite/common/data-models/service/viewstate-service-record';
import { PSTWEB_VIEW } from '@src/suite/common/enums/constants';
import { AppName, ViewStateTypeNames } from '@src/suite/common/enums/enums';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { IndexedDBService } from '@src/suite/common/services/indexeddb-service';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { NavigationService } from '@src/suite/common/services/navigation-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectCreateViewStateGroup, selectCreateViewStateSection } from '@src/suite/common/store/createviewstate/createviewstate.selectors';
import { selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectHomeProgram } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { ViewStateActions, ViewStateApiActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { getGroupByID, getGroupCopy, getGroupLayout, getTeam } from '@src/suite/common/utils/navigation-utils';
import { initializeViewStateRecord } from '@src/suite/common/utils/record-utils';
import { Util } from '@src/suite/common/utils/util';
import { getLayoutOptions } from '@src/suite/common/utils/viewstate-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { catchError, concatMap, exhaustMap, filter, forkJoin, from, map, mergeMap, of, switchMap, tap } from 'rxjs';

@Injectable()
export class NavigationEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    router = inject(Router);
    location = inject(Location);
    private navigationService = inject(NavigationService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService = inject(GlobalTablesService);
    languageSettings = inject(LanguageSettings);

    private visualStateService = inject(VisualStateService);
    private modal = inject(ModalService);
    private dialog = inject(MatDialog);
    private db = inject(IndexedDBService);

    updateNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.saveNavigation),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCurrentTeam)]),
            concatMap(([action, application, team]) => {
                const groups = action.groups.map(group => {
                    return {
                        application: application,
                        layout: getGroupLayout(group),
                        team: getTeam(group, team),
                        sections: getGroupCopy(group.Items),
                    };
                });
                return forkJoin(groups.map(group => this.navigationService.updateNavigation(group.application, group.layout, group.team, group.sections))).pipe(
                    mergeMap(result => {
                        let status = true;
                        if (result.some(res => res === false)) status = false;
                        return status ? of(NavigationApiActions.saveNavigationSuccess({ status: status })) : of(NavigationApiActions.saveNavigationError({ error: 'Failed to save navigation' }));
                    }),
                    catchError(error => of(NavigationApiActions.saveNavigationError({ error: error })))
                );
            })
        );
    });

    loadTeamNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.loadTeamNavigationData),
            concatLatestFrom(() => this.store.select(selectCurrentTeam)),
            switchMap(([action, team]) => {
                if (team === action.team) {
                    return of(ApplicationActions.noOpAction());
                }
                return this.navigationService.loadTeamNavigation(action.team).pipe(
                    map(res => {
                        return NavigationApiActions.loadTeamNavigationDataSuccess({ navigation: res, team: action.team });
                    }),
                    catchError(error => of(NavigationApiActions.loadTeamNavigationDataError({ error: error })))
                );
            })
        );
    });

    initializeTeamNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.loadTeamNavigationDataSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectTeamGroup), this.store.select(selectI18N)]),
            map(([action, application, group, i18n]) => {
                if (application === AppName.WASTEBIN) {
                    return ApplicationActions.noOpAction();
                }
                if (application === AppName.TEMPLATES) {
                    return ApplicationActions.noOpAction();
                }
                return NavigationActions.initializeTeamNavigationDataSuccess({
                    sections: this.initializeGroupSections(action.navigation[application], application, group.ID, group.Path, i18n),
                });
            })
        );
    });

    createNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.createNavigationItem),
            concatLatestFrom(() => [this.store.select(selectNavigationData), this.store.select(selectCreateViewStateGroup), this.store.select(selectCreateViewStateSection)]),
            concatMap(([action, state, group, section]) => {
                const vs = action.vs;
                let grp: NavigationGroup = null;
                let sc: NavigationSection = null;
                let it: NavigationItem = null;

                if (group == null || section == null) {
                    grp = state.navigation[state.application].find((g: NavigationGroup) => g.ID === NavigationGroupTypes.System);
                    if (grp) {
                        sc = grp.Items.find((s: NavigationSection) => s.ID === NavigationSectionTypes.System);
                        if (sc) {
                            it = sc.Items.find(i => i.Guid === NavigationItemTypes.SYSTEM_NEW_VIEW_GUID);
                            it = { ...it, Label: vs.Title, ViewState: vs };
                        }
                    }
                } else {
                    grp = state.navigation[state.application].find(g => g.ID === group.ID);
                    if (grp) {
                        sc = grp.Items.find(s => s.ID === section.ID);
                        if (sc) {
                            it = this.createNewNavigationItem(state.application, group, section, vs);
                        }
                    }
                }

                if (grp && sc && it && vs) {
                    return of(NavigationApiActions.createNavigationItemSuccess({ group: grp, section: sc, item: it, vs: vs }));
                } else {
                    return of(NavigationApiActions.createNavigationItemError({ error: 'Failed to create view state record' }));
                }
            })
        );
    });

    saveCurrentNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.saveCurrentNavigationItem),
            concatMap(() => {
                return this.navigationService.saveCurrentNavigationItem().pipe(
                    map(res => {
                        return NavigationApiActions.updateNavigationItemSuccess({ vs: res.data });
                    }),
                    catchError(error => of(NavigationApiActions.updateNavigationItemError({ error: error })))
                );
            })
        );
    });

    addNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.addNavigationItem),
            concatLatestFrom(() => this.store.select(selectNavigationData)),
            concatMap(([action, state]) => {
                let group: NavigationGroup = null;
                let section: NavigationSection = null;
                let item: NavigationItem = null;
                const vs: StorytellingViewStateRecord = action.vs;
                group = state.navigation[state.application].find((g: NavigationGroup) => g.ID === action.group);
                if (group) {
                    section = group.Items.find(s => s.ID === action.section);
                    if (section) {
                        item = this.createNewNavigationItem(state.application, group, section, vs);
                    }
                }
                if (group && section && item && vs) {
                    return of(NavigationApiActions.addNavigationItemSuccess({ group: group, section: section, item: item, vs: vs }));
                } else {
                    return of(NavigationApiActions.addNavigationItemError({ error: 'Failed to create view state record' }));
                }
            })
        );
    });

    addViewStateToNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.addViewStateToNavigation),
            exhaustMap(action => {
                const isOwnView = action.vs?.Path?.isOwnView;
                return this.dialog
                    .open(AddViewToNavigationComponent, {
                        hasBackdrop: true,
                        delayFocusTrap: true,
                        data: {
                            isPersonalView: isOwnView,
                            vs: action.vs,
                        },
                    })
                    .afterClosed();
            }),
            mergeMap((res: AddViewStateToNavigationResult) => {
                if (res?.action === ModalResponseCodes.CONFIRM) {
                    return of(NavigationApiActions.addNavigationItem({ group: res.group, section: res.section, vs: res.vs }));
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    updateNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.updateNavigationItem),
            concatMap(action => {
                return of(NavigationApiActions.updateNavigationItemSuccess({ vs: action.vs }));
            })
        );
    });

    createNavigationItemSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.createNavigationItemSuccess, NavigationApiActions.addNavigationItemSuccess),
            concatLatestFrom(() => [this.store.select(selectEnterpriseGroup), this.store.select(selectTeamGroup), this.store.select(selectPersonalGroup)]),
            concatMap(([action, enterprise, team, personal]) => {
                return from(this.router.navigate(action.item.Url)).pipe(
                    map(_ => {
                        const group = getGroupByID(action.group.ID, enterprise, team, personal);
                        if (group?.ID > 0) {
                            return NavigationApiActions.saveNavigation({
                                groups: [group],
                            });
                        }

                        return ApplicationActions.noOpAction();
                    }),
                    catchError(error => {
                        console.warn(error);
                        return of(NavigationApiActions.updateNavigationItemError({ error: error }));
                    })
                );
            })
        );
    });

    deleteNavigationSection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.deleteSection),
            concatLatestFrom(() => [this.store.select(selectI18N)]),
            exhaustMap(([action, i18n]) => {
                const params = {
                    group: action.group,
                    section: action.section,
                };
                return this.modal
                    .openModal({
                        title: i18n.PSTWebConfirmDeleteSectionLabel,
                        content: i18n.PSTWebConfirmDeleteSectionMessage,
                    })
                    .result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        map(() => params)
                    );
            }),
            switchMap(params => {
                return of(
                    NavigationActions.removeSection({
                        group: params.group,
                        section: params.section,
                    })
                );
            })
        );
    });

    updateNavigationSection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.updateSection, NavigationActions.removeSection),
            concatLatestFrom(() => [this.store.select(selectNavigationGroups)]),
            concatMap(([action, groups]) => {
                const group = groups.find(g => g.ID === action.group.ID);
                if (action.group.ID > 0) {
                    return of(
                        NavigationApiActions.saveNavigation({
                            groups: [group],
                        })
                    );
                }
            })
        );
    });

    private initializeGroupSections(sections: NavigationSection[], application: string, groupID: number, groupPath: string, i18n: Record<string, string>): NavigationSection[] {
        return sections.map((sec: NavigationSection) => {
            const translatedLabel = i18n[`PSTWebNavigationSection_${sec.Label}`] || sec.Label;
            const sectionPath: string = Util.url(translatedLabel);

            let sectionOpened = true;
            if (this.visualStateService.expanderSettingsMap()?.Navigation) {
                sectionOpened = this.visualStateService.expanderSettingsMap().Navigation[`${application}-${groupID}-${this.navigationService.currentPlaybookTeam}-${sec.ID}`];
                if (sectionOpened == null) sectionOpened = true;
            }

            const section: NavigationSection = {
                ...sec,
                Label: translatedLabel,
                Opened: sectionOpened,
                InEditMode: false,
                IsNewSection: false,
                PrevLabel: '',
                PrevOpened: false,
                Path: sectionPath,

                Items: sec.Items.map((i: NavigationItem, idx: number) => {
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(i.ViewState);
                    const viewState: StorytellingViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecordV2(serviceRecord);

                    let label = 'Undefined';
                    let url: string[] = [];
                    if (sec.ID !== NavigationSectionTypes.System && sec.ID !== NavigationSectionTypes.Default) {
                        if (viewState && viewState.Title) {
                            const lbl = `${PSTWEB_VIEW}_${i.Path}_${viewState.Title}`;
                            label = i18n[lbl] ?? viewState.Title;
                        }
                        url = ['/PST', application, groupPath, sectionPath, viewState.Guid];
                    }

                    return {
                        ...i,
                        Url: url,
                        Label: label,
                        Default: false,
                        Order: idx,
                        Hidden: false,
                        System: false,
                        Enterprise: viewState.Path.isEnterpriseView,
                        ViewState: viewState,
                        GroupPath: groupPath,
                        SectionPath: sectionPath,
                        SectionID: sec.ID,
                    };
                }),
            };

            return section;
        });
    }

    preInitializeNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.preInitializeNavigation),
            concatMap(() => {
                return this.db.many([NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID, NavigationItemTypes.SYSTEM_SEARCH_GUID]).pipe(
                    map(res => {
                        return res.filter(r => !!r);
                    }),
                    map(res => {
                        const params = {
                            current: null,
                            search: null,
                        };
                        for (const rec of res) {
                            if (rec?.DBGuid === NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID) {
                                const type = rec.PSTViewStateType;
                                const vs = new ViewStateServiceRecord(rec);
                                const record = initializeViewStateRecord<StorytellingViewStateRecord>(type);
                                this.nomWebServicesJson.convertor.deserializeV2(record, vs);
                                params.current = record;
                            }
                            if (rec?.DBGuid === NavigationItemTypes.SYSTEM_SEARCH_GUID) {
                                const type = rec.PSTViewStateType;
                                const vs = new ViewStateServiceRecord(rec);
                                const record = initializeViewStateRecord<StorytellingViewStateRecord>(type);
                                this.nomWebServicesJson.convertor.deserializeV2(record, vs);
                                params.search = record;
                            }
                        }
                        return NavigationActions.initializeNavigation(params);
                    }),
                    catchError(error => {
                        console.warn(error);
                        return of(NavigationActions.initializeNavigation({}));
                    })
                );
            })
        );
    });

    initializeNavigation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.initializeNavigation),
            concatLatestFrom(() => [this.store.select(selectNavigationRaw), this.store.select(selectHomeProgram), this.store.select(selectI18N)]),
            exhaustMap(([action, navigation, team, i18n]) => {
                const res: Navigation = {
                    cast: new Array<NavigationGroup>(),
                    discover: new Array<NavigationGroup>(),
                    playbook: new Array<NavigationGroup>(),
                    live: new Array<NavigationGroup>(),
                    wastebin: new Array<NavigationGroup>(),
                    templates: new Array<NavigationGroup>(),
                };
                for (const application in navigation) {
                    if (application === AppName.WASTEBIN || !res[application]) {
                        continue;
                    }
                    const systemGroup: NavigationGroup = {
                        ID: NavigationGroupTypes.System,
                        GroupId: 'System',
                        Path: 'system',
                        Label: ViewStateTypeNames.SYSTEM,
                        Order: -1,
                        Opened: false,
                        Hidden: true,
                        DefaultGuid: null,
                        Items: [],
                        System: true,
                        Team: false,
                    };

                    const systemSection: NavigationSection = {
                        ID: NavigationSectionTypes.System,
                        Label: ViewStateTypeNames.SYSTEM,
                        Order: -2,
                        Hidden: true,
                        Enterprise: false,
                        Items: [],
                        Opened: false,
                        InEditMode: false,
                        IsNewSection: false,
                        PrevLabel: ViewStateTypeNames.SYSTEM,
                        PrevOpened: false,
                        Path: 'system',
                    };
                    systemSection.Items = [
                        this.createSystemSearchNavigationItem(application, action.search as StorytellingSearchViewStateRecord), // Pass the stored search view state in action.search
                        this.createSystemCurrentNavigationItem(application, action.current),
                        this.createSystemNewNavigationItem(application),
                        this.createSystemSingleItemNavigationItem(application),
                    ];
                    systemGroup.Items.push(systemSection);
                    res[application].push(systemGroup);

                    navigation[application].forEach((g: NavigationGroup) => {
                        const translatedLabel = i18n[`PSTWebNavigationGroup_${g.Label}`] || g.Label;
                        const groupPath: string = Util.url(translatedLabel);

                        if (g.ID >= 0) {
                            let groupOpened = true;
                            if (this.visualStateService.expanderSettingsMap()?.Navigation) {
                                groupOpened = this.visualStateService.expanderSettingsMap().Navigation[`${application}-${g.ID}`];
                            }
                            const group: NavigationGroup = {
                                ...g,
                                Label: translatedLabel,
                                Opened: groupOpened,
                                System: g.ID < 0 ? true : false,
                                Team: g.ID === NavigationGroupTypes.Team,
                                Path: groupPath,
                                DefaultGuid: g.DefaultGuid,
                                Items: this.initializeGroupSections(g.Items, application, g.ID, groupPath, i18n),
                            };

                            res[application].push(group);
                        }
                    });
                }

                // Create system group, system section and system items for wastebin
                const wastebinSystemGroup: NavigationGroup = {
                    ID: NavigationGroupTypes.System,
                    GroupId: 'System',
                    Path: 'system',
                    Label: ViewStateTypeNames.SYSTEM,
                    Order: -1,
                    Opened: false,
                    Hidden: false,
                    DefaultGuid: null,
                    Items: [],
                    System: true,
                    Team: false,
                };

                const wastebinSystemSection = {
                    ID: NavigationSectionTypes.System,
                    Label: ViewStateTypeNames.SYSTEM,
                    Order: -2,
                    Hidden: true,
                    Enterprise: false,
                    Items: [],
                    Opened: false,
                    InEditMode: false,
                    IsNewSection: false,
                    PrevLabel: ViewStateTypeNames.SYSTEM,
                    PrevOpened: false,
                    Path: 'system',
                };

                wastebinSystemSection.Items.push(this.createSystemSearchNavigationItem(AppName.WASTEBIN));
                wastebinSystemSection.Items.push(this.createSystemWastebinNavigationItem(AppName.WASTEBIN));
                wastebinSystemGroup.Items.push(wastebinSystemSection);

                res[AppName.WASTEBIN].push(wastebinSystemGroup);

                // Create system group, system section and system items for templates
                const templatesSystemGroup: NavigationGroup = {
                    ID: NavigationGroupTypes.System,
                    GroupId: 'System',
                    Path: 'system',
                    Label: ViewStateTypeNames.SYSTEM,
                    Order: -1,
                    Opened: false,
                    Hidden: false,
                    DefaultGuid: null,
                    Items: [],
                    System: true,
                    Team: false,
                };

                const templatesSystemSection = {
                    ID: NavigationSectionTypes.System,
                    Label: ViewStateTypeNames.SYSTEM,
                    Order: -2,
                    Hidden: true,
                    Enterprise: false,
                    Items: [],
                    Opened: false,
                    InEditMode: false,
                    IsNewSection: false,
                    PrevLabel: ViewStateTypeNames.SYSTEM,
                    PrevOpened: false,
                    Path: 'system',
                };

                templatesSystemSection.Items.push(this.createSystemSearchNavigationItem(AppName.TEMPLATES));
                templatesSystemSection.Items.push(this.createSystemTemplatesNavigationItem(AppName.TEMPLATES));
                templatesSystemGroup.Items.push(templatesSystemSection);

                res[AppName.TEMPLATES].push(templatesSystemGroup);

                return of(NavigationActions.initializeNavigationSuccess({ navigation: res, team: team }));
            })
        );
    });

    initializeNavigationSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.initializeNavigationSuccess),
                map(() => {
                    this.navigationService.initializeResolve(null);
                })
            );
        },
        { dispatch: false }
    );

    navigateToDefaultItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.navigateToDefaultItem),
                concatLatestFrom(() => this.store.select(selectNavigateToURL)),
                map(([, urlToNavigate]) => {
                    if (urlToNavigate) {
                        this.router.navigate(urlToNavigate);
                    }
                })
            );
        },
        { dispatch: false }
    );

    navigateToItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.navigateToItem),
            concatLatestFrom(() => [
                this.store.select(selectNavigationLoaded),
                this.store.select(selectNavigateToGroup),
                this.store.select(selectNavigateToSection),
                this.store.select(selectNavigateToItem),
            ]),
            concatMap(([action, navigationLoaded, groupToNavigate, sectionToNavigate, itemToNavigate]) => {
                if (navigationLoaded) {
                    if (groupToNavigate && sectionToNavigate && itemToNavigate) {
                        // If the resolved item differs from what was requested, the item was not found and we fell back to default
                        if (action.item && action.item !== itemToNavigate) {
                            return of(NavigationActions.navigateToDefaultItem());
                        }
                        return of(NavigationActions.setCurrentItem({ group: groupToNavigate, section: sectionToNavigate, item: itemToNavigate }));
                    }
                    // Component-based navigation (e.g. Manage Team Members) has no group/section/item
                    if (action.component) {
                        return of(NavigationActions.setCurrentItem({ group: null, section: null, item: null }));
                    }
                    return of(NavigationActions.navigateToDefaultItem());
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    enterSearchMode$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.enterSearchMode),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    const group: NavigationGroup = state.navigation[state.application].find((grp: NavigationGroup) => grp.ID < 0);

                    if (group && group.Items && group.Items.length > 0) {
                        // Find system section
                        const section: NavigationSection = group.Items[0];

                        if (section && section.Items && section.Items.length > 0) {
                            const item: NavigationItem = section.Items.find(it => it.Guid === NavigationItemTypes.SYSTEM_SEARCH_GUID);

                            if (item) {
                                this.router.navigate(item.Url);
                            }
                        }
                    }
                })
            );
        },
        { dispatch: false }
    );

    enterWastebinMode$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.enterWastebinMode),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    const group: NavigationGroup = state.navigation[state.application].find((grp: NavigationGroup) => grp.ID < 0);

                    if (group && group.Items && group.Items.length > 0) {
                        // Find system section
                        const section: NavigationSection = group.Items[0];

                        if (section && section.Items && section.Items.length > 0) {
                            const item: NavigationItem = section.Items.find(it => it.Guid === NavigationItemTypes.SYSTEM_WASTEBIN_GUID);

                            if (item) {
                                this.router.navigate(item.Url);
                            }
                        }
                    }
                })
            );
        },
        { dispatch: false }
    );

    loadSingleNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.loadSingleNavigationItem),
            concatLatestFrom(() => [this.store.select(selectSystemGroup), this.store.select(selectSystemSection), this.store.select(selectSystemSingleViewStateItem)]),
            concatMap(([action, group, section, _item]) => {
                return [NavigationActions.navigateToItem({ group: group.Path, section: section.Path, item: action.guid }), RecordHttpActions.loadSingleItem({ guid: action.guid })];
            })
        );
    });

    setSingleNavigationItemData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.setSingleNavigationItemData),
            concatLatestFrom(() => [this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectSystemSingleViewStateItem)]),
            map(([action, i18n, pstGroupPrivs, item]) => {
                const layout = getLayoutOptions(action.itemtype, i18n, pstGroupPrivs);
                return ViewStateActions.setCurrentViewStateSuccess({ vs: item.ViewState, layout });
            })
        );
    });

    loadNavigationItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(NavigationActions.loadNavigationItem),
                concatLatestFrom(() => this.store.select(selectNavigationData)),
                tap(([_, state]) => {
                    if (state.item) {
                        this.router.navigate(state.item.Url);
                    }
                })
            );
        },
        { dispatch: false }
    );

    setDefaultNavigationItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationApiActions.setDefaultNavigationItem),
            concatLatestFrom(() => this.store.select(selectCurrentApplication)),
            concatMap(([action, application]) => {
                return from(this.navigationService.setDefaultNavigationItem(action.guid, action.layout, application, action.team)).pipe(
                    map(res => NavigationApiActions.setDefaultNavigationItemSuccess({ status: !!res.data, group: action.group, guid: action.guid })),
                    catchError(error => of(NavigationApiActions.setDefaultNavigationItemError({ error: error })))
                );
            })
        );
    });

    removeItemFromGroups$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.removeItemFromGroups),
            concatLatestFrom(() => [this.store.select(selectNavigationGroups)]),
            mergeMap(([action, navigation]) => {
                const groups: NavigationGroup[] = [];
                let found: NavigationSection = null;
                navigation.forEach(group => {
                    if (group.ID === NavigationGroupTypes.System) return;
                    found = group.Items.find(section => {
                        return section.Items.find(item => {
                            return item?.ViewState?.Guid === action.vs.Guid;
                        });
                    });
                    if (action.groups.find(g => g === group.ID)) groups.push(group);
                });

                if (!found && action.delete) {
                    return [ViewStateApiActions.deleteViewStateRecord({ vs: action.vs }), NavigationApiActions.saveNavigation({ groups: groups })];
                }

                return of(NavigationApiActions.saveNavigation({ groups: groups }));
            })
        );
    });

    setCurrentItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.setCurrentItem),
            concatLatestFrom(() => this.store.select(selectCurrentItem)),
            mergeMap(([_, item]) => {
                const vs = item && item.ViewState ? item.ViewState : null;
                return of(WireApiActions.stopPolling({ restart: false }), ViewStateActions.setCurrentViewState({ vs: vs }));
            })
        );
    });

    reorderItems$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigationActions.reorderItems),
            map(action => {
                return NavigationApiActions.saveNavigation({
                    groups: [action.group],
                });
            })
        );
    });

    deleteViewStateItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ViewStateApiActions.deleteViewStateRecord),
            concatLatestFrom(() => this.store.select(selectCurrentItem)),
            concatMap(([action, item]) => {
                if (action.vs?.Guid === item?.ViewState?.Guid) {
                    return of(NavigationActions.navigateToDefaultItem());
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    private searchViewStateTypeForApp: Map<string, ViewStateTypes> = new Map<string, ViewStateTypes>([
        [AppName.PLAYBOOK, ViewStateTypes.SEARCH],
        [AppName.LIVE, ViewStateTypes.LIVESEARCH],
        [AppName.DISCOVER, ViewStateTypes.WIRESEARCH],
        [AppName.WASTEBIN, ViewStateTypes.WASTEBINSEARCH],
        [AppName.TEMPLATES, ViewStateTypes.TEMPLATESSEARCH],
    ]);

    /**
     * Create system search navigation item for the specified app. If a view state is provided and its type matches the expected type for the app, it will be used; otherwise, a new view state will be created.
     * @param app the application name
     * @param vs the stored search view state record (in indexdb) to use, if any
     * @returns the created navigation item
     */
    private createSystemSearchNavigationItem(app: string, vs?: StorytellingSearchViewStateRecord): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const typeForApp = this.searchViewStateTypeForApp.get(app);

        // If no view state is provided or its type does not match the expected type for the app, initialize a new view state record
        // Create system search navigation item
        if (!vs || vs.PSTViewStateType !== typeForApp) {
            vs = initializeViewStateRecord(typeForApp);
            vs = {
                ...vs,
                Database: database,
                Path: new Path(''),
                Guid: NavigationItemTypes.SYSTEM_SEARCH_GUID,
                Title: ViewStateTypeNames.SEARCH,
                PSTViewStateFilterState: true,
                PSTViewStateSearchQuery: '',
                PSTViewStateFilter: ViewStateFilterSerializer.instance(this.globalTablesService).initialize(typeForApp),
                PSTViewStateSorting: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(typeForApp, PSTFieldNames.PSTViewStateSorting),
                PSTViewStateGrouping: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(typeForApp, PSTFieldNames.PSTViewStateGrouping),
            };
        }

        const item: NavigationItem = {
            Database: database,
            Path: null,
            Guid: vs.Guid,
            Hidden: false,
            Label: ViewStateTypeNames.SEARCH,
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: vs,
            Url: ['/PST', app, 'system', 'search', NavigationItemTypes.SYSTEM_SEARCH_GUID],
            IsDefault: false,
            Order: 1,
            System: true,
            Enterprise: false,
        };

        return item;
    }

    private createSystemWastebinNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();

        const vs = {
            ...initializeViewStateRecord(ViewStateTypes.WASTEBIN),
            Database: database,
            Path: new Path(''),
            Guid: NavigationItemTypes.SYSTEM_WASTEBIN_GUID,
            Title: ViewStateTypeNames.WASTEBIN,
            PSTViewStateFilterState: true,
            PSTViewStateFilter: ViewStateFilterSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.WASTEBIN),
            PSTViewStateSorting: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.WASTEBIN, PSTFieldNames.PSTViewStateSorting),
            PSTViewStateGrouping: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.WASTEBIN, PSTFieldNames.PSTViewStateGrouping),
        };

        const item: NavigationItem = {
            Database: database,
            Path: null,
            Guid: vs.Guid,
            Hidden: false,
            Label: ViewStateTypeNames.SEARCH,
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: vs,
            Url: ['/PST', app, 'system', 'wastebin', NavigationItemTypes.SYSTEM_WASTEBIN_GUID],
            IsDefault: false,
            Order: 1,
            System: true,
            Enterprise: false,
        };

        return item;
    }

    private createSystemTemplatesNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();

        const vs = {
            ...initializeViewStateRecord(ViewStateTypes.TEMPLATES),
            Database: database,
            Path: new Path(''),
            Guid: NavigationItemTypes.SYSTEM_TEMPLATES_GUID,
            Title: ViewStateTypeNames.TEMPLATES,
            PSTViewStateFilterState: true,
            PSTViewStateFilter: ViewStateFilterSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.TEMPLATES),
            PSTViewStateSorting: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.TEMPLATES, PSTFieldNames.PSTViewStateSorting),
            PSTViewStateGrouping: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.TEMPLATES, PSTFieldNames.PSTViewStateGrouping),
        };

        const item: NavigationItem = {
            Database: database,
            Path: null,
            Guid: vs.Guid,
            Hidden: false,
            Label: ViewStateTypeNames.SEARCH,
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: vs,
            Url: ['/PST', app, 'system', 'templates', NavigationItemTypes.SYSTEM_TEMPLATES_GUID],
            IsDefault: false,
            Order: 1,
            System: true,
            Enterprise: false,
        };

        return item;
    }

    private createSystemCurrentNavigationItem(app: string, vs?: StorytellingViewStateRecord): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            Database: database,
            Path: '',
            Guid: NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID,
            Hidden: false,
            Label: ViewStateTypeNames.CURRENT,
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: vs,
            Url: ['/PST', app, 'system', 'current', NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID],
            IsDefault: false,
            Order: 2,
            System: true,
            Enterprise: false,
        };
        return item;
    }

    private createSystemNewNavigationItem(app: string): NavigationItem {
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            Database: database,
            Path: '',
            Guid: NavigationItemTypes.SYSTEM_NEW_VIEW_GUID,
            Hidden: false,
            Label: ViewStateTypeNames.NEW,
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: null,
            Url: ['/PST', app, 'system', 'new', NavigationItemTypes.SYSTEM_NEW_VIEW_GUID],
            IsDefault: false,
            Order: 3,
            System: true,
            Enterprise: false,
        };

        return item;
    }

    private createSystemSingleItemNavigationItem(app: string): NavigationItem {
        const _i18n = this.store.selectSignal(selectI18N)();
        const database = this.nomWebServicesJson.getDatabase();
        const path = '';

        let vs: StorytellingSingleItemViewStateRecord = initializeViewStateRecord<StorytellingSingleItemViewStateRecord>(ViewStateTypes.SINGLEITEM);
        vs = {
            ...vs,
            Database: database,
            Path: new Path(path),
            Guid: NavigationItemTypes.SYSTEM_SINGLE_ITEM_VIEW_GUID,
            Title: 'Single Item',
            PSTViewStateSorting: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.SINGLEITEM, PSTFieldNames.PSTViewStateSorting),
            PSTViewStateGrouping: ViewStateOptionsSerializer.instance(this.globalTablesService).initialize(ViewStateTypes.SINGLEITEM, PSTFieldNames.PSTViewStateGrouping),
        };

        const item: NavigationItem = {
            Database: database,
            Path: path,
            Guid: vs.Guid,
            Hidden: false,
            Label: 'Item',
            SectionPath: 'system',
            GroupPath: 'system',
            SectionID: -1,
            ViewState: vs,
            Url: ['/PST', app, 'system', 'item', NavigationItemTypes.SYSTEM_SINGLE_ITEM_VIEW_GUID],
            IsDefault: false,
            Order: 4,
            System: true,
            Enterprise: false,
        };

        return item;
    }

    private createNewNavigationItem(app: string, group: NavigationGroup, section: NavigationSection, vs: StorytellingViewStateRecord): NavigationItem {
        const groupPath: string = Util.url(group.Label);
        const sectionPath: string = Util.url(section.Label);
        const database = this.nomWebServicesJson.getDatabase();
        const item: NavigationItem = {
            Database: database,
            Path: vs.Path.fullPath,
            Guid: vs.Guid,
            Hidden: false,
            Label: vs.Title,
            SectionPath: sectionPath,
            GroupPath: groupPath,
            SectionID: section.ID,
            ViewState: vs,
            Url: ['/PST', app, groupPath, sectionPath, vs.Guid],

            IsDefault: false,
            Order: -1,
            System: false,
            Enterprise: true,
        };

        return item;
    }
}
